'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface DeliveryPerson {
  name: string;
  phone: string;
  vehicleNumber: string;
  rating: number;
  image: string;
  alt: string;
}

interface DeliveryPersonCardProps {
  deliveryPerson: DeliveryPerson;
}

const DeliveryPersonCard = ({ deliveryPerson }: DeliveryPersonCardProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleCall = () => {
    if (isHydrated && typeof window !== 'undefined') {
      window.location.href = `tel:${deliveryPerson.phone}`;
    }
  };

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-lg shadow-elevation-md p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-muted rounded w-1/2 mb-4"></div>
          <div className="flex gap-4">
            <div className="w-16 h-16 bg-muted rounded-full"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-muted rounded w-1/3"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-elevation-md p-6">
      <h2 className="font-heading text-xl font-semibold text-foreground mb-4">
        Delivery Partner
      </h2>

      <div className="flex items-center gap-4 mb-6">
        <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
          <AppImage
            src={deliveryPerson.image}
            alt={deliveryPerson.alt}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1">
          <h3 className="font-medium text-foreground">{deliveryPerson.name}</h3>
          <div className="flex items-center gap-1 mt-1">
            <Icon name="StarIcon" size={16} className="text-warning" variant="solid" />
            <span className="font-data text-sm text-foreground">{deliveryPerson.rating}</span>
          </div>
          <p className="font-data text-sm text-muted-foreground mt-1">
            Vehicle: {deliveryPerson.vehicleNumber}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          onClick={handleCall}
          className="flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-md font-medium text-sm transition-smooth hover:shadow-elevation-md hover-lift"
        >
          <Icon name="PhoneIcon" size={18} />
          <span>Call Delivery Partner</span>
        </button>
        <button className="flex items-center justify-center gap-2 px-4 py-3 bg-muted text-foreground rounded-md font-medium text-sm transition-smooth hover:bg-border">
          <Icon name="ChatBubbleLeftRightIcon" size={18} />
          <span>Chat</span>
        </button>
      </div>
    </div>
  );
};

export default DeliveryPersonCard;